"use client";

import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function DataIntelligenceFutureHero() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const {
    ref: contentRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    let animationFrameId: number;
    let particles: Particle[] = [];
    let width = 0;
    let height = 0;
    class Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      radius: number;
      constructor(w: number, h: number) {
        this.x = Math.random() * w;
        this.y = Math.random() * h;
        this.vx = (Math.random() - 0.5) * 0.8;
        this.vy = (Math.random() - 0.5) * 0.8;
        this.radius = Math.random() * 1.5 + 0.5;
      }
      update(w: number, h: number) {
        this.x += this.vx;
        this.y += this.vy;
        if (this.x < 0 || this.x > w) this.vx = -this.vx;
        if (this.y < 0 || this.y > h) this.vy = -this.vy;
      }
      draw(context: CanvasRenderingContext2D) {
        context.beginPath();
        context.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        context.fillStyle = "rgba(255, 255, 255, 0.4)";
        context.fill();
      }
    }
    const init = () => {
      width = canvas.offsetWidth;
      height = canvas.offsetHeight;
      canvas.width = width;
      canvas.height = height;
      const particleCount = Math.min(Math.floor(width * height / 15000), 150);
      particles = Array.from({
        length: particleCount
      }, () => new Particle(width, height));
    };
    const animate = () => {
      ctx.clearRect(0, 0, width, height);
      for (let i = 0; i < particles.length; i++) {
        particles[i].update(width, height);
        particles[i].draw(ctx);
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < 120) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(255, 255, 255, ${0.15 - distance / 800})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      animationFrameId = requestAnimationFrame(animate);
    };
    init();
    animate();
    const handleResize = () => {
      init();
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);
  return <section data-section-id="121652" id="助聽器補助資格查詢-hero" className="dark bg-secondary text-foreground relative w-full h-[90vh] min-h-[600px] flex items-center justify-center overflow-hidden">
      {}
      <div className="absolute inset-0 bg-gradient-to-b from-secondary via-secondary/95 to-primary/30 pointer-events-none z-0" />

      {}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none z-0 opacity-40" />

      {}
      <div ref={contentRef} className={`relative z-10 container mx-auto px-4 md:px-6 flex flex-col items-center text-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-white mb-8">
          <Zap className="w-4 h-4 text-primary" />
          <span className="text-sm font-semibold tracking-wider">
            專業、快速、省心
          </span>
        </div>

        <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-6 tracking-tighter [text-shadow:_0_0_40px_rgba(255,255,255,0.2)] max-w-5xl">
          助聽器補助資格查詢
        </h1>

        <p className="text-lg md:text-2xl text-white/80 max-w-3xl mx-auto mb-10 leading-relaxed font-medium">
          專業評估與政府補助申辦指引，助您輕鬆減輕負擔，重拾清晰聽覺生活。我們陪伴您走過申請的每一步。
        </p>

        <div className="flex flex-col sm:flex-row gap-5 w-full sm:w-auto">
          <Button asChild size="lg" className="h-14 px-10 text-xl rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 shadow-xl transition-all hover:scale-105 active:scale-95">
            <Link to="https://interton.com.tw/聯絡我們/">
              立即填表諮詢
              <ArrowRight className="ml-2 w-6 h-6" />
            </Link>
          </Button>

          <Button asChild size="lg" variant="outline" className="h-14 px-10 text-xl rounded-xl border-white/30 text-white hover:bg-white/10 hover:text-white bg-transparent backdrop-blur-md transition-all hover:scale-105 active:scale-95">
            <Link to="/AddDesignSubsidyQualificationContentPage">了解補助流程</Link>
          </Button>
        </div>
      </div>

      {}
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-background to-transparent pointer-events-none z-0" />
    </section>;
}